import { call } from 'vuex-pathify';

import { Draw, Modify, Snap, Translate } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { MousePosition } from 'ol/control';
import { transform } from 'ol/proj.js';
import { getDistance } from 'ol/sphere.js';
import { circular } from 'ol/geom/Polygon.js';
import { GeoJSON } from 'ol/format';
import { GeometryCollection } from 'ol/geom';
import Feature from 'ol/Feature';

import { getGeometryCentroid } from '@/assets/js/mapUtils';
import { getContext } from '@/assets/js/mapContext';

import { v4 as uuid } from 'uuid';
import mapToolbar from '@/mixins/mapToolbar';

export default {
  mixins: [mapToolbar],
  methods: {
    addToolbarDrawInteraction(layer, type) {
      const drawInteraction = new Draw({
        source: layer.getSource(),
        ...(type === 'Circle' && {
          geometryFunction: (coords, geom, proj) => this.createPolygonFromCircle(coords, geom, proj, true),
        }),
        type,
        style: feature => this.getToolbarDrawStyle(feature, false, true),
      });
      drawInteraction.set('name', 'toolbarDrawInteraction');
      drawInteraction.on('drawend', e => {
        e.feature.setId(uuid());
        setTimeout(() => this.setToolbarGeometries());
      });
      this.map.addInteraction(drawInteraction);
    },
    addToolbarModifyInteraction(layer, type) {
      const modifyInteraction = new Modify({
        source: layer.getSource(),
        style: feature => this.getToolbarModifyStyleHandler(feature, type === 'Circle'),
      });
      modifyInteraction.set('name', 'toolbarModifyInteraction');
      modifyInteraction.on('modifyend', () => setTimeout(() => this.setToolbarGeometries()));
      if (type === 'Circle') {
        modifyInteraction.on('modifystart', event => {
          event.features.forEach(feature => {
            const geometry = feature.getGeometry();
            if (geometry.getType() === 'GeometryCollection') feature.set('modifyGeometry', geometry.clone(), true);
          });
        });

        modifyInteraction.on('modifyend', event => {
          event.features.forEach(feature => {
            const modifyGeometry = feature.get('modifyGeometry');
            if (modifyGeometry) {
              feature.setGeometry(modifyGeometry);
              feature.unset('modifyGeometry', true);
            }
          });
        });
      }
      this.map.addInteraction(modifyInteraction);
    },
    addToolbarTranslateInteraction(layer) {
      const translateInteraction = new Translate({
        layers: [layer],
        filter: feature => {
          const mouseCoordinateInEpsg = this.map
            .getControls()
            .getArray()
            .find(c => c instanceof MousePosition)
            ?.element.innerText.split(', ')
            .map(c => +c);
          const mouseCoordinate = transform(
            mouseCoordinateInEpsg,
            this.cursorPositionEpsg,
            this.$_config.defaultEpsg || 'EPSG:3857'
          );
          const mousePixel = this.map.getPixelFromCoordinate(mouseCoordinate);
          const featureCoordinate = getGeometryCentroid(feature.getGeometry());
          const featurePixel = this.map.getPixelFromCoordinate(featureCoordinate);
          return Math.abs(mousePixel[0] - featurePixel[0]) <= 20 && Math.abs(mousePixel[1] - featurePixel[1]) <= 20;
        },
      });
      translateInteraction.set('name', 'toolbarTranslateInteraction');
      translateInteraction.on('translateend', () => setTimeout(() => this.setToolbarGeometries()));
      this.map.addInteraction(translateInteraction);
    },
    addToolbarSnapInteraction(layer) {
      const snapInteraction = new Snap({
        source: layer.getSource(),
      });
      snapInteraction.set('name', 'toolbarSnapInteraction');
      this.map.addInteraction(snapInteraction);
    },
    addToolbarInteractions(layer, type) {
      this.addToolbarDrawInteraction(layer, type);
      this.addToolbarModifyInteraction(layer, type);
      this.addToolbarTranslateInteraction(layer);
      this.addToolbarSnapInteraction(layer);
    },
    addToolbarLayer({ skipTranslate = false } = {}) {
      const layer = new VectorLayer({
        source: new VectorSource({}),
        id: 'toolbar-draw',
        style: feature => this.getToolbarDrawStyle(feature, false, skipTranslate),
        zIndex: 1000,
        isSpecial: true,
      });
      this.map.addLayer(layer);
      return layer;
    },
    getToolbarGeometryGeojson() {
      const measurementFeaturesArray = this.getLayerById('toolbar-draw')?.getSource().getFeatures() || [];
      return {
        ...new GeoJSON().writeFeaturesObject(
          measurementFeaturesArray.map(f => {
            const geom = f.getGeometry();
            return geom instanceof GeometryCollection ? new Feature({ geometry: geom.getGeometries()[0] }) : f;
          })
        ),
        crs: {
          properties: {
            name: this.$_config.defaultEpsg,
          },
          type: 'name',
        },
      };
    },
    getToolbarDrawStyle(feature, disablePointStyle = false, disableTranslate = false) {
      const geometry = feature.getGeometry();
      const geometryType = geometry.getType();
      const gType = geometryType === 'GeometryCollection' ? 'Circle' : geometryType;
      return [
        ...(disablePointStyle ? [] : this.getPointMeasurementStyle({ geometry })),
        ...this.toolbarStyleFunctionsDict[gType]({
          geometry,
          feature,
          overlayId: 'toolbar',
          disableTranslate,
        }),
      ];
    },
    getToolbarModifyStyleHandler(feature, isCircle) {
      if (isCircle) {
        feature.get('features').forEach(modifyFeature => {
          const modifyGeometry = modifyFeature.get('modifyGeometry');
          if (modifyGeometry) {
            const modifyPoint = feature.getGeometry().getCoordinates();
            const geometries = modifyFeature.getGeometry().getGeometries();
            const polygon = geometries[0].getCoordinates()[0];
            const center = geometries[1].getCoordinates();
            const projection = this.map.getView().getProjection();
            let first, last, radius;
            if (modifyPoint[0] === center[0] && modifyPoint[1] === center[1]) {
              first = transform(polygon[0], projection, 'EPSG:4326');
              last = transform(polygon[(polygon.length - 1) / 2], projection, 'EPSG:4326');
              radius = getDistance(first, last) / 2;
            } else {
              first = transform(center, projection, 'EPSG:4326');
              last = transform(modifyPoint, projection, 'EPSG:4326');
              radius = getDistance(first, last);
            }
            const circle = circular(transform(center, projection, 'EPSG:4326'), radius, 128);
            circle.transform('EPSG:4326', projection);
            geometries[0].setCoordinates(circle.getCoordinates());
            modifyGeometry.setGeometries(geometries);
          }
        });
        return this.getToolbarDrawStyle(feature, true, true);
      } else {
        return this.getToolbarDrawStyle(feature, true, true);
      }
    },
    setToolbarGeometries() {
      const geojson = this.getToolbarGeometryGeojson();
      const types = Array.from(new Set(geojson.features.map(f => f.geometry.type.replace('Multi', ''))));
      this.toolbarGeometries = Object.freeze({ type: types.length === 1 ? types[0] : 'mixed', geojson });
    },

    clearToolbarDraw() {
      [
        this.getInteractionByName('toolbarDrawInteraction'),
        this.getInteractionByName('toolbarModifyInteraction'),
        this.getInteractionByName('toolbarSnapInteraction'),
        this.getInteractionByName('toolbarTranslateInteraction'),
      ].every(interaction => interaction && this.map.removeInteraction(interaction));
      ['toolbar-draw'].forEach(layerId => {
        const layer = this.getLayerById(layerId);
        if (layer) this.map.removeLayer(layer);
      });
      // this.turnOffModifySnapping(); //?
      this.removeOverlays({ id: 'toolbar' });
      this.toolbarGeometries = { type: null, geojson: null };
      if (this.activeTool?.startsWith('toolbar')) this.activeTool = null;
    },
    toggleToolbarDraw(e) {
      this.toggleToolbarSelection();
      this.deactivateAllToolsHandler();
      this.$nextTick(() => {
        const drawLayer = this.addToolbarLayer();
        this.addToolbarInteractions(drawLayer, e.type);
        this.activeTool = `toolbar-${e.type}`;
      });
    },

    getLayerFeature: call('layers/getLayerFeature'),
    toggleToolbarSelection({ value = false } = {}) {
      if (value) {
        getContext(this).$emit('toolbar-interactions', { names: ['draw', 'translate', 'modify', 'snap'], type: null });

        const callback = async (features, ctrlKey) => {
          const toolbarDrawFs = features.find(e => e.id === 'toolbar-draw');
          const f = toolbarDrawFs ? toolbarDrawFs.features[0] : features?.[0]?.features[0];
          if (f) {
            if (f.layerId === 'toolbar-draw') {
              const layer = this.getLayerById('toolbar-draw');
              const source = layer?.getSource();
              const feature = source.getFeatureById(f.id);

              if (ctrlKey) {
                this.removeOverlays({ id: `toolbar__${feature.ol_uid}` });
                if (feature) source.removeFeature(feature);
              } else {
                this.map.removeLayer(layer);
                this.removeOverlays({ id: 'toolbar' });

                setTimeout(() => {
                  let drawLayer = this.addToolbarLayer({ skipTranslate: true });
                  drawLayer.getSource().addFeature(feature);
                });
              }
            } else {
              let geometry = f.geometry || null;
              if (!geometry) {
                const feature = await this.getLayerFeature({ layer_id: f.layerId, feature_id: f.id });
                geometry = { ...feature.geometry, crs: feature.crs };
              }

              let drawLayer = this.getLayerById('toolbar-draw');
              if (drawLayer && !ctrlKey) {
                this.map.removeLayer(drawLayer);
                this.removeOverlays({ id: 'toolbar' });

                drawLayer = this.addToolbarLayer({ skipTranslate: true });
              }
              if (!drawLayer) drawLayer = this.addToolbarLayer({ skipTranslate: true });

              const feature = { geometry, properties: {}, type: 'Feature' };
              const fs = new GeoJSON().readFeature(feature);
              fs.setId(uuid());
              fs.set('relation', { layerId: f.layerId, featureId: f.id });

              drawLayer.getSource().addFeature(fs);
            }
            this.$nextTick(() => this.setToolbarGeometries());
          }
        };
        getContext(this).$emit('identification-action', true, {
          specialType: 'toolbarSelection',
          identifyCallback: (fts, { ctrlKey } = {}) => callback(fts, ctrlKey),
          layersFilter: layer => {
            return (
              (!layer.get('isSpecial') && !layer.get('isEditingLayer')) ||
              layer.get('customIdentificationable') === true ||
              layer.get('id') === 'toolbar-draw'
            );
          },
        });
      } else {
        getContext(this).$emit('identification-action', false);
      }
    },
  },
  mounted() {
    getContext(this).$on('toolbar-action', () => {
      getContext(this).$emit('toolbar-interactions', { names: ['draw', 'translate', 'modify', 'snap'], type: null });
    });
    getContext(this).$on('toolbar-toggle', e => {
      if (e.type) this.toggleToolbarDraw(e);
      else {
        this.clearToolbarDraw();
        this.toggleToolbarSelection();
      }
    });
    getContext(this).$on('toolbar-interactions', e => {
      const layer = this.getLayerById('toolbar-draw');
      if (e.type) {
        this.deactivateAllToolsHandler();
        e.names.forEach(name => {
          this[`addToolbar${this.$_capitalize(name)}Interaction`](layer, e.type);
          if (name === 'translate') layer.setStyle(feature => this.getToolbarDrawStyle(feature));
        });
        this.activeTool = `toolbar-${e.type}`;
      } else {
        e.names.forEach(name => {
          const interaction = this.getInteractionByName(`toolbar${this.$_capitalize(name)}Interaction`);
          if (interaction) this.map.removeInteraction(interaction);
          if (name === 'translate') layer?.setStyle(feature => this.getToolbarDrawStyle(feature, false, true));
        });
        this.activeTool = null;
      }
    });
    getContext(this).$on('toolbar-selection', e => this.toggleToolbarSelection(e));
  },
};
